import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ParentBottomTabBar } from '@/components/ui/ParentBottomTabBar';
import { ProposalSlotBadge } from '@/components/ui/ProposalSlotBadge';
import { useData } from '@/hooks/useData';
import { useProposals, useUpdateAcademicContext } from '@/services/children';
import { useHomeworks } from '@/services/homeworks';
import { apiFetch } from '@/services/apiGateway';
import {
  AUTHORITIES,
  COUNTRIES,
  getSchoolYears,
  getTrackConfig,
  normalizeSchoolYear,
} from '@/constants/education';
import { GlobalHeader } from '@/components/GlobalHeader';
import { AcademicYearResetModal } from '@/components/AcademicYearResetModal';
import { markTimetableRequired } from '@/utils/academicReset';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ProfileResponse {
  child_id: string;
  display_name: string | null;
  mode: 'high_school' | 'university' | null;
  school_year: string | null;
  country: string | null;
  education_authority: string | null;
  education_authorities: string[] | null;
  specializations: string[] | null;
  date_of_birth: string | null;
}

interface ActivityStats {
  flashcardsReviewed: number;
  quizAverage: number;
  explainTopics: string[];
}

const FORMAT_LABEL: Record<string, string> = {
  short: '15 min',
  standard: '30 min',
  deep: '60 min',
};

function getAuthorityDisplay(country: string | null, authorities: string[]): string {
  if (!country || authorities.length === 0) return '—';
  return authorities
    .map((code) => AUTHORITIES[country]?.find((a) => a.code === code)?.name ?? code)
    .join(', ');
}

function getCountryDisplay(code: string | null): string {
  if (!code) return '—';
  const found = COUNTRIES.find((c) => c.code === code);
  return found ? `${found.flag} ${found.name}` : code;
}

function calcAge(dob: string): number {
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ParentChildDetailView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useParams<{ childId: string }>();

  const { data: profile, isLoading: loadingProfile, refetch: refetchProfile } = useData<ProfileResponse>(
    childId ? `/children/${childId}/profile` : null,
  );
  const { data: stats, isLoading: loadingStats } = useData<ActivityStats>(
    childId ? `/children/${childId}/activity-stats` : null,
  );
  const { data: proposalsData, isLoading: loadingProposals } = useProposals(childId ?? '');

  const proposals = proposalsData?.proposals ?? [];
  const isLoading = loadingProfile || loadingStats;

  const { data: homeworksData } = useHomeworks(childId ?? '');
  const homeworks = homeworksData?.homeworks ?? [];
  const openHomeworks = homeworks.filter((h) => h.status === 'open');

  // Homework carousel state
  const hwCarouselRef = useRef<HTMLDivElement>(null);
  const [activeHwSlide, setActiveHwSlide] = useState(0);
  const handleHwScroll = () => {
    if (!hwCarouselRef.current) return;
    setActiveHwSlide(Math.round(hwCarouselRef.current.scrollLeft / 292));
  };

  // Homework stats per subject
  const subjectStats = homeworks.reduce<Record<string, { total: number; done: number }>>((acc, hw) => {
    const subj = hw.subject;
    if (!acc[subj]) acc[subj] = { total: 0, done: 0 };
    acc[subj].total += hw.exercises.length;
    acc[subj].done += hw.exercises.filter((e) => e.status === 'done').length;
    return acc;
  }, {});
  const homeworkSubjects = Object.entries(subjectStats);

  // ── Profile inline edit ────────────────────────────────────────────────────
  // Editing country or level of study here also triggers the destructive
  // academic-year reset (US-PRF-01/US-PRF-02) — wipes timetable/free-time/
  // commitments — so those two fields are confirmed via AcademicYearResetModal
  // before saving; editing authority alone saves immediately, no confirmation.
  const [isEditing, setIsEditing] = useState(false);
  const [editCountry, setEditCountry] = useState('');
  const [editStudyLevel, setEditStudyLevel] = useState('');
  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>([]);
  const [authorityFilter, setAuthorityFilter] = useState('');
  const [editSpecializations, setEditSpecializations] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const updateAcademicContext = useUpdateAcademicContext(childId ?? '');

  useEffect(() => {
    if (profile) {
      setEditCountry(profile.country ?? 'FR');
      setEditStudyLevel(normalizeSchoolYear(profile.school_year ?? ''));
      const authorities =
        profile.education_authorities?.length
          ? profile.education_authorities
          : profile.education_authority
            ? [profile.education_authority]
            : [];
      setSelectedAuthorities(authorities);
      setEditSpecializations(profile.specializations ?? []);
    }
  }, [profile]);

  const handleCountryChange = (newCountry: string) => {
    setEditCountry(newCountry);
    setEditStudyLevel('');
    setSelectedAuthorities([]);
    setAuthorityFilter('');
    setEditSpecializations([]);
  };

  const handleStudyLevelChange = (newLevel: string) => {
    setEditStudyLevel(newLevel);
    setEditSpecializations([]);
  };

  const toggleAuthority = (code: string) => {
    setSelectedAuthorities((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleSpecialization = (name: string) => {
    setEditSpecializations((prev) => {
      if (prev.includes(name)) return prev.filter((s) => s !== name);
      if (editTrackConfig && prev.length >= editTrackConfig.maxSelect) return prev;
      return [...prev, name];
    });
  };

  const academicContextChanged =
    editCountry !== (profile?.country ?? 'FR') ||
    editStudyLevel !== normalizeSchoolYear(profile?.school_year ?? '');

  const handleSaveProfile = async () => {
    if (!childId) return;
    if (academicContextChanged) {
      setShowResetModal(true);
      return;
    }
    setIsSaving(true);
    try {
      await apiFetch(`/children/${childId}/profile`, {
        method: 'PUT',
        body: JSON.stringify({
          country: editCountry,
          education_authorities: selectedAuthorities,
          specializations: editSpecializations,
        }),
      });
      setIsEditing(false);
      refetchProfile();
    } catch {
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmAcademicReset = async () => {
    if (!childId) return;
    try {
      await updateAcademicContext.mutateAsync({
        country_code: editCountry,
        study_level: editStudyLevel,
        education_authorities: selectedAuthorities,
      });
      // Specializations aren't part of the academic-context reset payload (they
      // don't invalidate the timetable) — save them via the plain profile update
      // right after, so a track pick made in this same edit isn't lost.
      await apiFetch(`/children/${childId}/profile`, {
        method: 'PUT',
        body: JSON.stringify({ specializations: editSpecializations }),
      });
      markTimetableRequired(childId);
      setShowResetModal(false);
      navigate(`/parent/child/${childId}/timetable/scan`);
    } catch {
      setShowResetModal(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditCountry(profile?.country ?? 'FR');
    setEditStudyLevel(normalizeSchoolYear(profile?.school_year ?? ''));
    const authorities =
      profile?.education_authorities?.length
        ? profile.education_authorities
        : profile?.education_authority
          ? [profile.education_authority]
          : [];
    setSelectedAuthorities(authorities);
    setAuthorityFilter('');
    setEditSpecializations(profile?.specializations ?? []);
  };

  const allAuthorities = AUTHORITIES[editCountry] ?? [];
  const showFilter = allAuthorities.length > 4;
  const filteredAuthorities = showFilter
    ? allAuthorities.filter(
        (a) =>
          a.name.toLowerCase().includes(authorityFilter.toLowerCase()) ||
          a.code.toLowerCase().includes(authorityFilter.toLowerCase()),
      )
    : allAuthorities;

  const profileAuthorities =
    profile?.education_authorities?.length
      ? profile.education_authorities
      : profile?.education_authority
        ? [profile.education_authority]
        : [];

  const isUniversity = profile?.mode === 'university';
  const modeLabel = isUniversity
    ? t('add_child.university')
    : profile?.date_of_birth
      ? t('parent_student.age_label', { age: calcAge(profile.date_of_birth) })
      : t('add_child.high_school');

  const academicStudyLevels = getSchoolYears(
    editCountry,
    isUniversity ? 'university' : 'high_school',
  );
  const editTrackConfig = !isUniversity ? getTrackConfig(editCountry, editStudyLevel) : null;

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/parent" />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          {/* ── Global loading skeleton ── */}
          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="loading-skeleton" style={{ height: '120px', borderRadius: '12px' }} />
              <div className="loading-skeleton" style={{ height: '100px', borderRadius: '12px' }} />
              <div className="loading-skeleton" style={{ height: '80px', borderRadius: '12px' }} />
            </div>
          )}

          {!isLoading && (
            <>
              {/* ── Pillar 1: Profile Card ── */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
                  {profile?.display_name ?? t('parent_dash.student_fallback')}
                </h1>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <span style={{
                    background: 'var(--primary-light)', color: 'var(--primary)',
                    fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: '999px',
                  }}>
                    {modeLabel}
                  </span>
                  {profile?.school_year && (
                    <span style={{
                      background: '#F3F4F6', color: '#4B5563',
                      fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: '999px',
                    }}>
                      {profile.school_year}
                    </span>
                  )}
                </div>

                {!isEditing ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dark)' }}>
                        <span style={{ color: 'var(--text-muted)', marginRight: '6px' }}>{t('parent_student.country')}:</span>
                        {getCountryDisplay(profile?.country ?? null)}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dark)' }}>
                        <span style={{ color: 'var(--text-muted)', marginRight: '6px' }}>{t('parent_student.authority')}:</span>
                        {getAuthorityDisplay(profile?.country ?? null, profileAuthorities)}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setIsEditing(true)}
                      style={{ width: '100%', fontSize: '0.9rem' }}
                    >
                      ✏️ {t('parent_student.edit_profile')}
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px' }}>

                      {/* Country */}
                      <div>
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                          {t('parent_student.country')}
                        </label>
                        <select
                          className="input"
                          value={editCountry}
                          onChange={(e) => handleCountryChange(e.target.value)}
                        >
                          <option value="" disabled>{t('parent_student.select_country')}</option>
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Level of Study */}
                      <div>
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                          {t('academic_context.study_level')}
                        </label>
                        <select
                          className="input"
                          value={editStudyLevel}
                          onChange={(e) => handleStudyLevelChange(e.target.value)}
                        >
                          <option value="" disabled>{t('academic_context.select_study_level')}</option>
                          {academicStudyLevels.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>

                      {/* Track / specialization multi-select (e.g. French spécialités) */}
                      {editTrackConfig && (
                        <div>
                          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                            {editTrackConfig.label}
                          </label>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 6px' }}>
                            {t('add_child.track_helper', { count: editTrackConfig.maxSelect })}
                          </p>
                          <div style={{
                            border: '1px solid var(--border)', borderRadius: '8px', padding: '4px 0',
                          }}>
                            {editTrackConfig.options.map((name) => {
                              const checked = editSpecializations.includes(name);
                              const disabledOption = !checked && editSpecializations.length >= editTrackConfig.maxSelect;
                              return (
                                <label
                                  key={name}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    padding: '8px 12px', cursor: disabledOption ? 'not-allowed' : 'pointer',
                                    opacity: disabledOption ? 0.5 : 1,
                                    background: checked ? 'var(--primary-light)' : 'transparent',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleSpecialization(name)}
                                    disabled={disabledOption}
                                    style={{ accentColor: 'var(--primary)', width: '16px', height: '16px' }}
                                  />
                                  <span style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>{name}</span>
                                </label>
                              );
                            })}
                          </div>
                          {editSpecializations.length > 0 && (
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                              {t('add_child.selected', { count: editSpecializations.length })}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Authority multi-select */}
                      {allAuthorities.length > 0 && (
                        <div>
                          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                            {t('parent_student.authority')}
                          </label>
                          {showFilter && (
                            <input
                              type="text"
                              className="input"
                              placeholder={t('actions.filter_authorities')}
                              value={authorityFilter}
                              onChange={(e) => setAuthorityFilter(e.target.value)}
                              style={{ marginBottom: '6px' }}
                            />
                          )}
                          <div style={{
                            maxHeight: '180px', overflowY: 'auto',
                            border: '1px solid var(--border)', borderRadius: '8px',
                            padding: '4px 0',
                          }}>
                            {filteredAuthorities.map((a) => (
                              <label
                                key={a.code}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '10px',
                                  padding: '8px 12px', cursor: 'pointer',
                                  background: selectedAuthorities.includes(a.code)
                                    ? 'var(--primary-light)'
                                    : 'transparent',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedAuthorities.includes(a.code)}
                                  onChange={() => toggleAuthority(a.code)}
                                  style={{ accentColor: 'var(--primary)', width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>
                                  {a.name}
                                </span>
                              </label>
                            ))}
                            {filteredAuthorities.length === 0 && (
                              <p style={{ padding: '8px 12px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                {t('parent_student.no_authority_match')}
                              </p>
                            )}
                          </div>
                          {selectedAuthorities.length > 0 && (
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                              {t('add_child.selected', { count: selectedAuthorities.length })}
                            </p>
                          )}
                        </div>
                      )}

                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleCancelEdit}
                        style={{ flex: 1 }}
                      >
                        {t('actions.cancel')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveProfile}
                        disabled={isSaving || selectedAuthorities.length === 0 || !editStudyLevel}
                        style={{ flex: 1 }}
                      >
                        {isSaving ? t('actions.saving') : t('parent_student.save')}
                      </button>
                    </div>
                  </>
                )}
              </div>

              <AcademicYearResetModal
                open={showResetModal}
                isSaving={updateAcademicContext.isPending}
                onCancel={() => setShowResetModal(false)}
                onConfirm={handleConfirmAcademicReset}
              />

              {/* ── Pillar 2: Activity & Progress ── */}
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('parent_student.progress')}
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 600 }}>
                    {t('parent_student.flashcards')}
                  </p>
                  <h3 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)', margin: 0 }}>
                    {stats?.flashcardsReviewed ?? 0}
                  </h3>
                </div>
                <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 600 }}>
                    {t('parent_student.quiz_avg')}
                  </p>
                  <h3 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success)', margin: 0 }}>
                    {stats?.quizAverage ?? 0}%
                  </h3>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 10px' }}>
                  {t('parent_student.topics')}
                </p>
                {(stats?.explainTopics ?? []).length === 0 ? (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
                    {t('parent_student.no_topics')}
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {stats!.explainTopics.map((topic) => (
                      <span
                        key={topic}
                        style={{
                          background: 'var(--primary-light)', color: 'var(--primary)',
                          fontSize: '0.75rem', fontWeight: 500,
                          padding: '4px 10px', borderRadius: '999px',
                        }}
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Pillar 3: Today's Study Proposals ── */}
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('parent_student.today_proposals')}
              </h2>

              {loadingProposals && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                  <div className="loading-skeleton" style={{ height: '80px', borderRadius: '12px' }} />
                  <div className="loading-skeleton" style={{ height: '80px', borderRadius: '12px' }} />
                </div>
              )}

              {!loadingProposals && proposals.length === 0 && (
                <div className="card" style={{ marginBottom: '24px' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0 }}>
                    {t('parent_student.no_proposals')}
                  </p>
                </div>
              )}

              {!loadingProposals && proposals.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                  {proposals.map((p, i) => (
                    <div key={i} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem' }}>
                          {p.subject}
                        </span>
                        <span style={{
                          background: 'var(--primary-light)', color: 'var(--primary)',
                          fontSize: '0.72rem', fontWeight: 600,
                          padding: '3px 10px', borderRadius: '999px', flexShrink: 0,
                        }}>
                          {FORMAT_LABEL[p.format] ?? `${p.duration_minutes} min`}
                        </span>
                      </div>
                      {p.free_time_slot && <ProposalSlotBadge slot={p.free_time_slot} />}
                      <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                        {p.rationale}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Pillar 4: Homework ── */}
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('homework.title')}
              </h2>

              {homeworks.length === 0 ? (
                <div className="card" style={{ marginBottom: '24px' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0 }}>
                    {t('parent_student.no_homework')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Open homework carousel */}
                  {openHomeworks.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>
                        {t('parent_student.open_label')}
                      </p>
                      <div style={{ margin: '0 -16px' }}>
                        <div
                          ref={hwCarouselRef}
                          className="no-scrollbar"
                          onScroll={handleHwScroll}
                          style={{
                            display: 'flex',
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            scrollBehavior: 'smooth',
                            WebkitOverflowScrolling: 'touch',
                            gap: '12px',
                            padding: '0 16px 8px 16px',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                          } as React.CSSProperties}
                        >
                          {openHomeworks.map((hw) => {
                            const doneCount = hw.exercises.filter((e) => e.status === 'done').length;
                            const total = hw.exercises.length;
                            const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
                            return (
                              <div
                                key={hw.homework_id}
                                style={{ width: '280px', maxWidth: '85vw', flexShrink: 0, scrollSnapAlign: 'center' }}
                              >
                                <div className="card h-full" style={{ display: 'flex', flexDirection: 'column' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{hw.subject}</span>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                      {doneCount}/{total} {t('homework.done')}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px', flex: 1 }}>
                                    {hw.themes.map((th) => (
                                      <span
                                        key={th}
                                        style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '999px', background: 'var(--primary-light)', color: 'var(--primary)' }}
                                      >
                                        {th}
                                      </span>
                                    ))}
                                  </div>
                                  <div style={{ width: '100%', background: '#E5E7EB', borderRadius: '999px', height: '6px' }}>
                                    <div style={{ width: `${pct}%`, background: 'var(--primary)', height: '6px', borderRadius: '999px', transition: 'width 0.3s' }} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {openHomeworks.length > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '4px' }}>
                          {openHomeworks.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              aria-label={t('parent_student.homework_aria', { n: idx + 1 })}
                              onClick={() => hwCarouselRef.current?.scrollTo({ left: idx * 292, behavior: 'smooth' })}
                              style={{
                                width: activeHwSlide === idx ? '20px' : '8px', height: '8px',
                                borderRadius: '4px', border: 'none',
                                background: activeHwSlide === idx ? 'var(--primary)' : '#D1D5DB',
                                transition: 'all 0.2s', cursor: 'pointer', padding: 0,
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Homework statistics per subject */}
                  {homeworkSubjects.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <p style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>
                        {t('parent_student.homework_completed')}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {homeworkSubjects.map(([subject, s]) => {
                          const pct = s.total > 0 ? Math.round((s.done / s.total) * 100) : 0;
                          return (
                            <div key={subject} style={{ background: 'white', borderRadius: '12px', padding: '14px 16px', border: '1px solid var(--border)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)' }}>{subject}</span>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                  {s.done}/{s.total} {t('homework.exercise')}s
                                </span>
                              </div>
                              <div style={{ width: '100%', background: '#E5E7EB', borderRadius: '999px', height: '6px' }}>
                                <div style={{ width: `${pct}%`, background: pct === 100 ? 'var(--success)' : 'var(--primary)', height: '6px', borderRadius: '999px', transition: 'width 0.3s' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Pillar 5: Weekly Schedule ── */}
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('parent_student.weekly_schedule')}
              </h2>

              <div className="card" style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 16px' }}>
                  {t('parent_student.schedule_desc')}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => navigate(`/parent/child/${childId}/timetable`)}
                >
                  📅 {t('parent_student.view_schedule')}
                </button>
              </div>

            </>
          )}

        </div>

        <ParentBottomTabBar activeTab="Family" />
      </div>
    </div>
  );
}
